import { useCallback, useEffect, useState } from "react";
import { AddLink } from "../design/AddLink";
import { Link, useNavigate, useParams } from "react-router-dom";

import { send } from "../api/auth";
import { console_, type Worker, type WorkerStatusOption } from "../api/console";
import { LoadFailed } from "../design/LoadFailed";
import { StatusChip } from "../design/StatusChip";
import { toneOf, type Tone } from "../design/tone";
import { useT } from "../i18n/LocaleProvider";
import { translateLabel } from "../i18n/translate";
import { Tabs } from "../design/Tabs";
import { WorkerBenchAvailability } from "./Availability";
import { useSession } from "../session/SessionProvider";
import type { Failure } from "../system/failure";
import { WorkerCredentials } from "./Credentials";
import { CredentialExceptions } from "./CredentialExceptions";
import { WorkerProfilePanel } from "./WorkerProfile";

// ---------------------------------------------------------------------------
// The bench, in the console.
//
// FOUR RULES THIS FILE FOLLOWS.
//
// 1. THE STATUS SELECT HAS NO DEFAULT, AND THAT IS A SECURITY DECISION.
//
//    V5 shipped an invite form whose role <select> selected the first option,
//    and the first option was `owner` — so "invite a colleague" defaulted to
//    handing over the organisation. The general rule that came out of it is the
//    one applied here: ANY CONTROL WHOSE DEFAULT GRANTS CAPABILITY HAS NO
//    DEFAULT, and the submit button stays disabled until somebody chooses.
//
//    A bench status grants capability. `is_assignable` is what V13 will ask
//    before it may offer a person work, so a form that quietly picked one would
//    be deciding whether an unvetted person is assignable. `rank` cannot be used
//    to derive a safe answer either — it is a display order, not a capability
//    ordering, exactly as it was for roles.
//
// 2. THE VOCABULARY COMES FROM THE SERVER. There is no <option> written here
//    and no list of status ids anywhere in this file. Absolute rule 4.
//
// 3. STATUS IS NEVER COLOUR ALONE. Every state goes through StatusChip, which
//    derives its icon from the tone so a caller cannot omit it and refuses an
//    empty label.
//
// 4. ARCHIVED, NOT DELETED, and there is no button that says otherwise. V13
//    assignments and V22 Proof Packs point at these rows.
//
// WHAT IS DELIBERATELY ABSENT: a notes box. Absolute rule 5 — there is no column
// to write it to, and this is the screen where somebody would have asked for one.
// ---------------------------------------------------------------------------

// A failed read keeps its status, so a limit and a fault read differently (design/LoadFailed).
type Loaded<T> = { state: "loading" } | { state: "error"; failure: Failure } | { state: "ready"; data: T };

/**
 * The tone a bench status is shown in, derived from what the status MEANS rather
 * than from its id.
 *
 * `is_assignable` is the server's answer to "can this person be given work",
 * so a new status invented in a config pack gets a sensible colour without this
 * file being edited. Nothing here is red: somebody who is not yet on the bench
 * is not an error, and reserving red for failure is what makes it mean anything.
 */
function statusTone(option: WorkerStatusOption | undefined): Tone {
  if (!option) return "neutral";
  return option.is_assignable ? "live" : "neutral";
}

function useWorkerStatuses(): WorkerStatusOption[] {
  const { apiBase } = useSession();
  const [statuses, setStatuses] = useState<WorkerStatusOption[]>([]);
  useEffect(() => {
    let live = true;
    void (async () => {
      try {
        const { status, body } = await send<{ worker_statuses?: WorkerStatusOption[] }>(
          console_.workerStatuses(apiBase),
        );
        if (live && status === 200 && body.worker_statuses) setStatuses(body.worker_statuses);
      } catch {
        // The select stays empty and the form stays unusable, which is visible.
        // Inventing a status would silently decide whether somebody is
        // assignable, which is not.
      }
    })();
    return () => {
      live = false;
    };
  }, [apiBase]);
  return statuses;
}

function labelFor(statuses: WorkerStatusOption[], id: string): string {
  // Falls back to the raw id rather than to an empty string. V5's finding 3 was
  // exactly this lookup missing and rendering nothing useful; an id on screen is
  // ugly and truthful, and StatusChip refuses an empty label anyway.
  return statuses.find((s) => s.id === id)?.label ?? id;
}

// ===========================================================================
// The bench list — search, filter, and one page at a time.
// ===========================================================================

export function WorkerList() {
  const { apiBase, me } = useSession();
  const { t, refusal, locale } = useT();
  const statuses = useWorkerStatuses();

  // `query` is what is typed; `term` is what has been searched for. Keeping them
  // apart is what stops a request per keystroke, and it means the heading can
  // say what the list is actually showing rather than what somebody is midway
  // through typing.
  const [query, setQuery] = useState("");
  const [term, setTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [loaded, setLoaded] = useState<Loaded<Worker[]>>({ state: "loading" });
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  // The real total from the server (UX pass, 22 October 2026: "1-50 of 1 234", never only "Show more").
  const [total, setTotal] = useState<number | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);

  const load = useCallback(async () => {
    setLoaded({ state: "loading" });
    setNextCursor(null);
    try {
      const { status, body } = await send<{
        workers?: Worker[];
        next_cursor?: string | null;
        total?: number;
        error?: { message?: string };
      }>(console_.listWorkers(apiBase, { q: term, status: statusFilter }));
      if (status !== 200 || !body.workers) {
        setLoaded({ state: "error", failure: { status, refusal: refusal(body.error) } });
        return;
      }
      setLoaded({ state: "ready", data: body.workers });
      setNextCursor(body.next_cursor ?? null);
      setTotal(typeof body.total === "number" ? body.total : null);
    } catch {
      setLoaded({ state: "error", failure: { status: 0, refusal: t("We could not reach Verbunk.") } });
    }
  }, [apiBase, term, statusFilter]);

  useEffect(() => {
    void load();
  }, [load]);

  async function loadMore() {
    if (!nextCursor || loadingMore || loaded.state !== "ready") return;
    setLoadingMore(true);
    try {
      const { status, body } = await send<{ workers?: Worker[]; next_cursor?: string | null }>(
        console_.listWorkers(apiBase, { q: term, status: statusFilter, cursor: nextCursor }),
      );
      if (status === 200 && body.workers) {
        const more = body.workers;
        setLoaded((prev) => (prev.state === "ready" ? { state: "ready", data: [...prev.data, ...more] } : prev));
        setNextCursor(body.next_cursor ?? null);
      }
    } catch {
      // The page already on screen is still correct. A failed "load more" that
      // wiped it would lose the person's place in a two-thousand-row bench.
    } finally {
      setLoadingMore(false);
    }
  }

  const mayManage = me?.authenticated === true && me.permissions.includes("worker.manage");

  const anyReference = loaded.state === "ready" && loaded.data.some((w) => w.reference);
  return (
    <section className="panel">
      <div className="panel-head">
        <h2>{t("Bench")}</h2>
        {mayManage ? (
          <AddLink to="/console/workers/new" label={t("Add someone")} />
        ) : null}
      </div>

      <form
        className="row"
        onSubmit={(event) => {
          event.preventDefault();
          setTerm(query.trim());
        }}
      >
        <div className="field">
          <label htmlFor="worker-search">{t("Search by name or reference")}</label>
          <input
            id="worker-search"
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={t("Start typing a name")}
          />
        </div>
        <div className="field">
          <label htmlFor="worker-status-filter">{t("Status")}</label>
          <select
            id="worker-status-filter"
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
          >
            {/* A FILTER MAY DEFAULT AND A FORM MAY NOT. "Everyone" is the widest
                view of what this person is already entitled to see, so it grants
                nothing; the status on a NEW record decides whether somebody can
                be given work, so it is chosen deliberately. */}
            <option value="">{t("Everyone")}</option>
            {statuses.map((s) => (
              <option key={s.id} value={s.id}>
                {translateLabel(locale, s.label)}
              </option>
            ))}
          </select>
        </div>
        <button className="btn" type="submit">
          <svg aria-hidden="true" width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
            <circle cx="7" cy="7" r="4.5" />
            <path d="M10.5 10.5 14 14" />
          </svg>
          {t("Search")}
        </button>
      </form>

      {loaded.state === "loading" ? <p className="muted">{t("Loading…")}</p> : null}

      {loaded.state === "error" ? (
        <LoadFailed failure={loaded.failure} fallback={t("We could not load the bench.")} onRetry={() => void load()} />
      ) : null}

      {loaded.state === "ready" && loaded.data.length === 0 ? (
        <p className="muted">
          {term || statusFilter
            ? t("Nobody on the bench matches that.")
            : t("Nobody on the bench yet. These are the people you send to work — events, offers and timesheets all hang off one.")}
        </p>
      ) : null}

      {loaded.state === "ready" && loaded.data.length > 0 ? (
        <>
          <table className="data-table">
            <caption className="visually-hidden">{t("People on this organisation’s bench")}</caption>
            <thead>
              <tr>
                <th scope="col">{t("Name")}</th>
                {anyReference ? <th scope="col">{t("Reference")}</th> : null}
                <th scope="col">{t("Where")}</th>
              </tr>
            </thead>
            <tbody>
              {loaded.data.map((worker) => (
                <tr key={worker.id}>
                  <th scope="row">
                    <Link to={`/console/workers/${worker.id}`}>{worker.display_name}</Link>
                    {/* Only what is off is said: a chip on every row is a column of the same word. */}
                    {worker.has_account ? null : <StatusChip tone="neutral" label={t("No sign-in yet")} />}
                    {statusTone(statuses.find((s) => s.id === worker.status)) === "live" ? null : (
                      <StatusChip
                        tone={statusTone(statuses.find((s) => s.id === worker.status))}
                        label={translateLabel(locale, labelFor(statuses, worker.status))}
                      />
                    )}
                  </th>
                  {anyReference ? <td className="numeric">{worker.reference ?? "—"}</td> : null}
                  <td>{worker.home_locality ?? worker.home_postcode ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {total !== null && loaded.data.length > 0 ? (
            <p className="muted">{t("{shown} of {total} on the bench", { shown: loaded.data.length, total })}</p>
          ) : null}
          {nextCursor ? (
            <div className="row">
              <button className="btn btn-quiet" type="button" onClick={() => void loadMore()} disabled={loadingMore}>
                {loadingMore ? t("Loading…") : t("Show more")}
              </button>
            </div>
          ) : null}
        </>
      ) : null}
    </section>
  );
}

// ===========================================================================
// Add somebody
// ===========================================================================

export function NewWorker() {
  const { apiBase } = useSession();
  const { t, refusal, locale } = useT();
  const navigate = useNavigate();
  const statuses = useWorkerStatuses();

  const [givenName, setGivenName] = useState("");
  const [familyName, setFamilyName] = useState("");
  const [reference, setReference] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [postcode, setPostcode] = useState("");
  const [locality, setLocality] = useState("");
  // NO DEFAULT. See rule 1 at the top of this file.
  const [status, setStatus] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const contactable = email.trim() !== "" || phone.trim() !== "";
  const complete = givenName.trim() !== "" && familyName.trim() !== "" && status !== "" && contactable;

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (busy || !complete) return;
    setBusy(true);
    setError(null);
    try {
      const { status: code, body } = await send<{ worker?: Worker; error?: { message?: string } }>(
        console_.createWorker(apiBase, {
          given_name: givenName,
          family_name: familyName,
          reference: reference || null,
          email: email || null,
          phone: phone || null,
          home_postcode: postcode || null,
          home_locality: locality || null,
          status,
        }),
      );
      if (code !== 201 || !body.worker) {
        setError(refusal(body.error) ?? t("That did not work. Please try again."));
        return;
      }
      navigate(`/console/workers/${body.worker.id}`, { replace: true });
    } catch {
      setError(t("We could not reach Verbunk. Check your connection and try again."));
    } finally {
      setBusy(false);
    }
  }

  const chosen = statuses.find((s) => s.id === status);

  return (
    <section className="panel">
      <div className="panel-head">
        <h2>{t("Add someone to the bench")}</h2>
      </div>

      <form onSubmit={submit}>
        <div className="field">
          <label htmlFor="worker-given">{t("First name")}</label>
          <input id="worker-given" value={givenName} onChange={(e) => setGivenName(e.target.value)} required maxLength={100} />
        </div>
        <div className="field">
          <label htmlFor="worker-family">{t("Last name")}</label>
          <input id="worker-family" value={familyName} onChange={(e) => setFamilyName(e.target.value)} required maxLength={100} />
        </div>
        <div className="field">
          <label htmlFor="worker-reference">{t("Your reference")}</label>
          <input id="worker-reference" value={reference} onChange={(e) => setReference(e.target.value)} maxLength={60} />
          <p className="field-hint">{t("Optional. Your own code for this person, if you use one.")}</p>
        </div>

        <div className="field">
          <label htmlFor="worker-email">{t("Email")}</label>
          <input id="worker-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} maxLength={320} />
        </div>
        <div className="field">
          <label htmlFor="worker-phone">{t("Phone")}</label>
          <input id="worker-phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} maxLength={24} />
          <p className="field-hint">{t("Give at least one of email or phone — it is how offers reach them.")}</p>
        </div>

        <div className="field">
          <label htmlFor="worker-locality">{t("Town or city")}</label>
          <input id="worker-locality" value={locality} onChange={(e) => setLocality(e.target.value)} maxLength={120} />
        </div>
        <div className="field">
          <label htmlFor="worker-postcode">{t("Postcode")}</label>
          <input id="worker-postcode" value={postcode} onChange={(e) => setPostcode(e.target.value)} maxLength={12} />
          <p className="field-hint">
            {t("Used to work out travel time to a venue. Verbunk does not store a street address.")}
          </p>
        </div>

        <div className="field">
          <label htmlFor="worker-status">{t("Status")}</label>
          <select id="worker-status" value={status} onChange={(e) => setStatus(e.target.value)} required>
            {/* NO PRESELECTED OPTION, and the empty one is not a choice — the
                submit button below stays disabled until a real status is picked.
                See rule 1 at the top of this file. */}
            <option value="">{t("Choose a status…")}</option>
            {statuses.map((s) => (
              <option key={s.id} value={s.id}>
                {translateLabel(locale, s.label)}
              </option>
            ))}
          </select>
          <p className="field-hint">
            {chosen
              ? translateLabel(locale, chosen.description)
              : t("This decides whether they can be given work. Pick the one that is true today.")}
          </p>
        </div>

        {error ? (
          <div className="form-message" role="alert">
            <StatusChip tone="failed" label={t("Not saved")} />
            <span>{error}</span>
          </div>
        ) : null}

        <div className="row">
          <button className="btn" type="submit" disabled={busy || !complete}>
            {busy ? t("Saving…") : t("Add to bench")}
          </button>
          <Link className="btn btn-quiet" to="/console/workers">
            {t("Cancel")}
          </Link>
        </div>
      </form>
    </section>
  );
}

// ===========================================================================
// One person
// ===========================================================================

export function WorkerDetail() {
  const { id = "" } = useParams();
  const { apiBase, me } = useSession();
  const { t, refusal, locale } = useT();
  const statuses = useWorkerStatuses();
  const [loaded, setLoaded] = useState<Loaded<Worker>>({ state: "loading" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const load = useCallback(async () => {
    setLoaded({ state: "loading" });
    try {
      const { status, body } = await send<{ worker?: Worker; error?: { message?: string } }>(
        console_.getWorker(apiBase, id),
      );
      if (status !== 200 || !body.worker) {
        setLoaded({ state: "error", failure: { status, refusal: refusal(body.error) } });
        return;
      }
      setLoaded({ state: "ready", data: body.worker });
    } catch {
      setLoaded({ state: "error", failure: { status: 0, refusal: t("We could not reach Verbunk.") } });
    }
  }, [apiBase, id]);

  useEffect(() => {
    void load();
  }, [load]);

  const mayManage = me?.authenticated === true && me.permissions.includes("worker.manage");

  async function invite() {
    if (loaded.state !== "ready") return;
    setBusy(true);
    setError(null);
    try {
      const { status, body } = await send<{ error?: { message?: string } }>(
        console_.inviteWorker(apiBase, loaded.data.id),
      );
      if (status !== 201) {
        setError(refusal(body.error) ?? t("The invitation could not be sent."));
        return;
      }
      await load();
    } catch {
      setError(t("We could not reach Verbunk. Check your connection and try again."));
    } finally {
      setBusy(false);
    }
  }

  async function changeStatus(next: string) {
    if (busy || next === "") return;
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      const { status, body } = await send<{ worker?: Worker; error?: { message?: string } }>(
        console_.updateWorker(apiBase, id, { status: next }),
      );
      if (status !== 200 || !body.worker) {
        setError(refusal(body.error) ?? t("That did not work."));
        return;
      }
      // The update route does not derive the sign-in state; the status change does not move it.
      const signIn = loaded.state === "ready" ? loaded.data.sign_in : undefined;
      setLoaded({ state: "ready", data: { ...body.worker, sign_in: signIn } });
      setSaved(true);
    } catch {
      setError(t("We could not reach Verbunk."));
    } finally {
      setBusy(false);
    }
  }

  if (loaded.state === "loading") return <p className="muted">{t("Loading…")}</p>;
  if (loaded.state === "error") {
    return (
      <section className="panel">
        <LoadFailed failure={loaded.failure} fallback={t("We could not load this person.")} onRetry={() => void load()} />
        <Link className="btn btn-quiet" to="/console/workers">
          {t("Back to the bench")}
        </Link>
      </section>
    );
  }

  const worker = loaded.data;
  const current = statuses.find((s) => s.id === worker.status);

  return (
    <div className="stack">
      <Link className="crumb" to="/console/workers">
        ← {t("Back to the bench")}
      </Link>
      <section className="panel">
      <div className="panel-head">
        <h2>{worker.display_name}</h2>
        <StatusChip tone={statusTone(current)} label={translateLabel(locale, labelFor(statuses, worker.status))} />
      </div>

      <dl className="facts facts-compact">
        <div>
          <dt>{t("Reference")}</dt>
          <dd>{worker.reference ?? "—"}</dd>
        </div>
        <div>
          <dt>{t("Email")}</dt>
          <dd>{worker.email ?? "—"}</dd>
        </div>
        <div>
          <dt>{t("Phone")}</dt>
          <dd>{worker.phone ?? "—"}</dd>
        </div>
        <div>
          <dt>{t("Town or city")}</dt>
          <dd>{worker.home_locality ?? "—"}</dd>
        </div>
        <div>
          <dt>{t("Postcode")}</dt>
          <dd>{worker.home_postcode ?? "—"}</dd>
        </div>
        {/* S41 (0193): the state's words, tone and invite verb come from its row;
            only the detail read sends it, so a row without it shows nothing here. */}
        {worker.sign_in ? (
          <div>
            <dt>{t("Can they sign in")}</dt>
            <dd>
              <StatusChip tone={toneOf(worker.sign_in.tone)} label={translateLabel(locale, worker.sign_in.label)} />
              {/* SWEEP S5. The bench record's own email is invited; resending mints a new link
                  and retires the old one. */}
              {mayManage && worker.sign_in.invite_label ? (
                <button
                  type="button"
                  className="btn btn-quiet"
                  disabled={busy || worker.email === null}
                  title={worker.email === null ? t("Add an email address first — the invitation goes there.") : undefined}
                  onClick={() => void invite()}
                >
                  {worker.sign_in.invite_label}
                </button>
              ) : null}
            </dd>
          </div>
        ) : null}
      </dl>

      {current ? <p className="muted">{translateLabel(locale, current.description)}</p> : null}

      {mayManage ? (
        <form
          className="row"
          onSubmit={(event) => {
            event.preventDefault();
          }}
        >
          <div className="field">
            <label htmlFor="worker-status-change">{t("Change status")}</label>
            <select
              id="worker-status-change"
              // THE CURRENT VALUE IS THE ONLY DEFENSIBLE PRESELECTION: it is what
              // is already true, so leaving it alone changes nothing. This is not
              // the case rule 1 forbids — there the form had no prior state and
              // picked one for you.
              value={worker.status}
              disabled={busy}
              onChange={(event) => void changeStatus(event.target.value)}
            >
              {statuses.map((s) => (
                <option key={s.id} value={s.id}>
                  {translateLabel(locale, s.label)}
                </option>
              ))}
            </select>
          </div>
        </form>
      ) : null}

      {saved ? (
        <div className="form-message" role="status">
          <StatusChip tone="live" label={t("Saved")} />
          <span>{t("Their status has been changed.")}</span>
        </div>
      ) : null}

      {error ? (
        <div className="form-message" role="alert">
          <StatusChip tone="failed" label={t("Not saved")} />
          <span>{error}</span>
        </div>
      ) : null}

      </section>

      {mayManage ? <EmploymentDetails worker={worker} onSaved={() => void load()} /> : null}

      <Tabs
        label={t("About this person")}
        tabs={[
          {
            id: "credentials",
            label: t("Credentials"),
            // V7. Rendered for anybody who may see the bench; the panel itself hides the
            // recording form from anybody without document.manage, and the API refuses it
            // regardless. S11: named, time-limited exceptions to a credential.
            content: (
              <div className="stack">
                <WorkerCredentials workerId={worker.id} />
                <CredentialExceptions workerId={worker.id} />
              </div>
            ),
          },
          // Sweep §4 / 0179. Languages, emergency contact, skills.
          { id: "profile", label: t("Profile"), content: <WorkerProfilePanel workerId={worker.id} /> },
          // S3. This person's availability, in the record rather than a screen of its own.
          { id: "availability", label: t("Availability"), content: <WorkerBenchAvailability embedded /> },
        ]}
      />
    </div>
  );
}

/** Answers 52, 57, 64: what the agency records about this person's engagement and home area. */
function EmploymentDetails({ worker, onSaved }: { worker: Worker; onSaved: () => void }) {
  const { apiBase } = useSession();
  const { locale, t } = useT();
  const [types, setTypes] = useState<{ id: string; label: string; description: string }[]>([]);
  const [type, setType] = useState(worker.employment_type ?? "");
  const [birth, setBirth] = useState(worker.date_of_birth ?? "");
  const [locality, setLocality] = useState(worker.home_locality ?? "");
  const [postcode, setPostcode] = useState(worker.home_postcode ?? "");
  const [said, setSaid] = useState<{ ok: boolean; text: string } | null>(null);

  useEffect(() => {
    void send<{ employment_types?: { id: string; label: string; description: string }[] }>(
      console_.employmentTypes(apiBase),
    ).then((res) => setTypes(res.body.employment_types ?? []));
  }, [apiBase]);

  async function save(event: React.FormEvent) {
    event.preventDefault();
    const res = await send<{ error?: { message?: string } }>(
      console_.updateWorker(apiBase, worker.id, {
        employment_type: type,
        date_of_birth: birth || null,
        home_locality: locality || null,
        home_postcode: postcode || null,
      }),
    );
    if (res.status === 200) {
      setSaid({ ok: true, text: t("Saved.") });
      onSaved();
    } else setSaid({ ok: false, text: res.body.error?.message ?? t("This could not be saved.") });
  }

  return (
    <section className="panel">
      <h3>{t("Engagement and home area")}</h3>
      <form onSubmit={(e) => void save(e)}>
        <div className="field">
          <label htmlFor="worker-type">{t("Works as")}</label>
          <select id="worker-type" value={type} onChange={(e) => setType(e.target.value)}>
            {types.map((k) => (
              <option key={k.id} value={k.id}>{translateLabel(locale, k.label)}</option>
            ))}
          </select>
          <p className="field-hint">{t("Your agency decides this and answers for it. A contractor must be genuine: the label does not cure disguised employment.")}</p>
        </div>
        <div className="field">
          <label htmlFor="worker-birth">{t("Date of birth")}</label>
          <input id="worker-birth" type="date" value={birth} onChange={(e) => setBirth(e.target.value)} />
          <p className="field-hint">{t("Used only to apply the rules for people under 18, which end on their 18th birthday.")}</p>
        </div>
        <div className="field">
          <label htmlFor="worker-area-town">{t("Town or city")}</label>
          <input id="worker-area-town" value={locality} maxLength={120} onChange={(e) => setLocality(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="worker-area-postcode">{t("Postcode")}</label>
          <input id="worker-area-postcode" value={postcode} maxLength={12} onChange={(e) => setPostcode(e.target.value)} />
          <p className="field-hint">{t("Only shows travel time and sorts a list. It never scores or excludes anybody.")}</p>
        </div>
        <button className="btn" type="submit">{t("Save")}</button>
      </form>
      {said ? <StatusChip tone={said.ok ? "live" : "failed"} label={said.text} /> : null}
    </section>
  );
}
